/**
 * "Seen" for a one-time feature introduction (the multi-run introduction,
 * EN-10791; the heads-up Sit & Go introduction, EN-10748): one storage shape,
 * each introduction under its own prefix.
 *
 * Written when the PLAYER dismisses the modal (×, a button, Escape, a backdrop
 * tap after the guard) — never when it merely paints, and never on a teardown
 * (location going invalid, the flag turning off, a clock starting at a table),
 * because the rule reads "after dismissal, never again".
 *
 * Keyed by EDITION and playerGUID:
 *   - per edition, so each announcement is its own one-time showing. The
 *     edition id is validated at the flag boundary (lower-case letters,
 *     digits, dashes), so it is safe inside the key.
 *   - per player, the LaunchDarkly convention for any client preference a
 *     flagged behavior stores, so a shared device never passes one player's
 *     dismissal to another.
 *
 * Best effort, per install: the web app and the native app keep separate
 * storage, and iOS may reclaim WebView storage.
 */

/**
 * 'unreadable' fails CLOSED at the call site: if storage cannot be read, the
 * introduction does not show. The promo fails open, which is how a private
 * window gets it on every visit; a one-time announcement must not repeat.
 */
export type FeatureIntroSeenState = 'seen' | 'unseen' | 'unreadable';

export type FeatureIntroSeen = {
  read: (edition: string, playerGUID: string) => FeatureIntroSeenState;
  /** False when storage refused the write; the caller's session guard still holds. */
  mark: (edition: string, playerGUID: string) => boolean;
};

export const createFeatureIntroSeen = (prefix: string): FeatureIntroSeen => {
  const keyFor = (edition: string, playerGUID: string): string =>
    `${prefix}${edition}:${playerGUID}`;

  return {
    read: (edition, playerGUID) => {
      if (!edition || !playerGUID) return 'unreadable';
      try {
        return window.localStorage.getItem(keyFor(edition, playerGUID)) === '1' ? 'seen' : 'unseen';
      } catch {
        return 'unreadable';
      }
    },
    mark: (edition, playerGUID) => {
      if (!edition || !playerGUID) return false;
      try {
        window.localStorage.setItem(keyFor(edition, playerGUID), '1');
        return true;
      } catch {
        return false;
      }
    }
  };
};
